import { useEffect, useState } from "react";
import { ErrorNotice } from "../components/AdminFeedback";
import { useLocale } from "../i18n";
import { eventRequest, operation } from "./api";
import type { RecordValue } from "./types";

export function ResultComputationProgress({ jobId, onReview }: { jobId: string; onReview?: () => void }) {
  const { w } = useLocale();
  const [job, setJob] = useState<RecordValue | null>(null);
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    setJob(null);
    if (!/^[A-Za-z0-9][A-Za-z0-9_.-]{0,95}$/.test(jobId)) {
      setError(new Error("Missing computation reference"));
      return () => controller.abort();
    }
    async function load() {
      if (controller.signal.aborted) return;
      setBusy(true);
      try {
        const value = await eventRequest(operation("resultJob"), { jobId }, undefined, controller.signal);
        if (controller.signal.aborted) return;
        if (value.jobId !== jobId || !["QUEUED", "RUNNING", "COMPLETE", "FAILED"].includes(String(value.state)) ||
          (value.state === "COMPLETE" && (typeof value.snapshotId !== "string" || !value.snapshotId))) throw new Error("Invalid computation status");
        setJob(value); setError(undefined);
        if (["QUEUED", "RUNNING"].includes(String(value.state))) timer = setTimeout(() => void load(), 5000);
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause);
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    }
    void load();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [jobId, refresh]);
  const complete = job?.state === "COMPLETE";
  const failed = job?.state === "FAILED";
  const missingScore = failed && job.failureCode === "EVENT_MISSING_SCORE";
  return <div className="command-result event-computation-progress">
    <p className={complete ? "success-box" : failed ? "error-box" : "info-box"} role={failed ? "alert" : "status"}>
      {complete ? w("اكتمل الاحتساب وحُفظت اللقطة. راجعها قبل نشرها؛ لم تُنشر تلقائياً.", "Computation is complete and its snapshot is saved. Review it before publishing; it was not published automatically.")
        : failed ? w("تعذّر إكمال الاحتساب. راجع حالة المهمة قبل بدء محاولة جديدة.", "Computation failed. Review the job status before starting another attempt.")
        : error ? w("قُبل طلب الاحتساب، لكن لم يتأكد اكتماله. حدّث حالة المهمة دون بدء احتساب جديد.", "The computation request was accepted, but completion is unconfirmed. Refresh this job without starting a new computation.")
        : w("تم قبول طلب الاحتساب، وما زالت النتيجة قيد التجهيز. نتابع المهمة تلقائياً حتى اكتمال اللقطة.", "The computation request was accepted and the result is being prepared. Its status refreshes automatically until the snapshot is complete.")}
    </p>
    {missingScore && <p className="error-box">{w("لا يمكن احتساب النتيجة لوجود تقييم مطلوب أو مصدر تقييم غير مكتمل. راجع اكتمال تقييمات المشاركات والمصادر المطلوبة؛ لم تُنشأ لقطة نتيجة لهذه المهمة.", "The result cannot be computed because a required evaluation or evaluation source is incomplete. Review the required entry evaluations and sources; this job did not create a result snapshot.")}</p>}
    <p>{w("مرجع مهمة الاحتساب:", "Computation reference:")} <bdi>{jobId}</bdi></p>
    {failed && job.failureCode && <p>{w("سبب التعثر:", "Failure code:")} <bdi>{String(job.failureCode)}</bdi></p>}
    <ErrorNotice error={error} />
    {complete && onReview ? <button type="button" className="secondary" onClick={onReview}>{w("مراجعة لقطة النتيجة", "Review result snapshot")}</button>
      : <button type="button" className="secondary" disabled={busy} onClick={() => setRefresh(value => value + 1)}>{w("تحديث حالة الاحتساب", "Refresh computation status")}</button>}
  </div>;
}
